/**
 * Referral bridge — KOL attribution.
 *
 * On first paint, look at `?ref=X` in `window.location`. If present, persist
 * to `localStorage[REFERRAL_KEY]` and emit `{ code, source: "url" }` to Elm.
 * If absent, look up the persisted value and emit `{ code, source: "storage" }`
 * if found. If neither, do nothing.
 *
 * Keeps the captured URL parameter pristine for the user's clipboard — strips
 * the `?ref=` from the bar via `history.replaceState` so subsequent shares
 * don't accidentally re-attribute (and the URL looks clean).
 *
 * The `code` is normalised: lowercased, trimmed, restricted to a safe slug
 * shape (`[a-z0-9-]{1,40}`). Anything else is dropped on the floor — this is
 * a public URL parameter, treat as untrusted input.
 */

import type { Elm as ElmModule } from "*.elm";

type ElmPorts = ReturnType<typeof ElmModule.Main.init>["ports"];

const REFERRAL_KEY = "mc.referrer.v1";
const REFERRAL_PARAM = "ref";
const SLUG_PATTERN = /^[a-z0-9-]{1,40}$/u;

type Source = "url" | "storage";

type StoredReferral = {
  readonly code: string;
  readonly capturedAtMs: number;
  readonly source: Source;
};

export const wireReferralBridge = (ports: ElmPorts): void => {
  if (ports.referralLoaded === undefined) return;

  const url = new URL(globalThis.location.href);
  const fromUrl = normaliseSlug(url.searchParams.get(REFERRAL_PARAM));

  if (fromUrl !== null) {
    const stored: StoredReferral = {
      code: fromUrl,
      capturedAtMs: Date.now(),
      source: "url",
    };
    persist(stored);
    cleanUrl(url);
    ports.referralLoaded.send({ code: fromUrl, source: "url" });
    return;
  }

  const persisted = readPersisted();
  if (persisted !== null) {
    ports.referralLoaded.send({ code: persisted.code, source: "storage" });
  }
};

/**
 * Read the captured referral synchronously. Used by `checkout-bridge.ts` to
 * include the referrer in `CreateSessionRequest` even if the original URL no
 * longer carries `?ref=`.
 */
export const readReferralCode = (): string | null => {
  const persisted = readPersisted();
  return persisted === null ? null : persisted.code;
};

const normaliseSlug = (raw: string | null): string | null => {
  if (raw === null) return null;
  const candidate = raw.trim().toLowerCase();
  return SLUG_PATTERN.test(candidate) ? candidate : null;
};

const persist = (value: StoredReferral): void => {
  try {
    globalThis.localStorage.setItem(REFERRAL_KEY, JSON.stringify(value));
  } catch {
    // Quota / private mode — silently swallow; attribution is best-effort.
  }
};

const readPersisted = (): StoredReferral | null => {
  try {
    const raw = globalThis.localStorage.getItem(REFERRAL_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    return validateStoredReferral(parsed);
  } catch {
    return null;
  }
};

const validateStoredReferral = (raw: unknown): StoredReferral | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const code = typeof r["code"] === "string" ? r["code"] : null;
  const capturedAtMs =
    typeof r["capturedAtMs"] === "number" ? r["capturedAtMs"] : null;
  if (code === null || capturedAtMs === null) return null;
  if (!SLUG_PATTERN.test(code)) return null;
  return { code, capturedAtMs, source: "storage" };
};

const cleanUrl = (url: URL): void => {
  url.searchParams.delete(REFERRAL_PARAM);
  const search = url.searchParams.toString();
  const next = `${url.pathname}${search === "" ? "" : `?${search}`}${url.hash}`;
  globalThis.history.replaceState(null, "", next);
};
